import { RESOURCE_LOCATION, ResourceLocation } from "../../Constants/resourceLocations";
import ReferenceModel from "../../Models/ReferenceModel";
import { IKnowledgeArticleSectionModel } from "../../Services/KnowledgeArticleSectionService";
import { ALERT_LIST_PATH, alertDetailPath } from "../Alert/alertConstants";
import { MARKETPLACE_ROUTE, marketplaceConnectionPath } from "../Marketplace/marketplaceConstants";
import { OBJECTIVE_LIST_PATH, objectiveDetailPath } from "../Objective/objectiveRoutes";
import { PROCESSFLOW_LIST_PATH, processflowDetailPath } from "../Processflow/processflowConstants";
import { PROJECT_LIST_PATH, projectDetailPath } from "../Project/projectConstants";
import { KNOWLEDGE_BASE_LIST_PATH, knowledgeArticleDetailPath } from "./knowledgeBaseConstants";

export const KNOWLEDGE_ARTICLE_RELATION_TYPES = [
    RESOURCE_LOCATION.KNOWLEDGE_ARTICLE,
    RESOURCE_LOCATION.ALERT,
    RESOURCE_LOCATION.DASHBOARD,
    RESOURCE_LOCATION.DATASOURCE,
    RESOURCE_LOCATION.DATASERVICE,
    RESOURCE_LOCATION.PROCESSFLOW,
    RESOURCE_LOCATION.OBJECTIVE,
    RESOURCE_LOCATION.PROJECT,
] as const;

export type KnowledgeArticleRelationType = (typeof KNOWLEDGE_ARTICLE_RELATION_TYPES)[number];

/** Stored as {@code locationIdentifier} when the article is linked to a module overview, not one item. */
export const KNOWLEDGE_ARTICLE_RELATION_OVERVIEW = "overview";

/** Client-only id for a relation staged in the manage dialog until Save. */
export const KNOWLEDGE_ARTICLE_RELATION_DRAFT_PREFIX = "draft:";

export const isKnowledgeArticleRelationDraft = (uuid?: string | null): boolean =>
    Boolean(uuid?.startsWith(KNOWLEDGE_ARTICLE_RELATION_DRAFT_PREFIX));

export const createKnowledgeArticleRelationDraftId = (): string =>
    `${KNOWLEDGE_ARTICLE_RELATION_DRAFT_PREFIX}${crypto.randomUUID()}`;

/** {@code dialog}: behind the page's info button, opened in a dialog. {@code link}: the in-page display —
 *  listed in the target's own page by {@code LinkedKnowledgeArticlesInPage}. */
export const KNOWLEDGE_ARTICLE_RELATION_DISPLAY = {
    DIALOG: "dialog",
    LINK: "link",
} as const;

export type KnowledgeArticleRelationDisplay =
    (typeof KNOWLEDGE_ARTICLE_RELATION_DISPLAY)[keyof typeof KNOWLEDGE_ARTICLE_RELATION_DISPLAY];

export const isKnowledgeArticleRelationOverview = (identifier?: string | null): boolean =>
    identifier?.trim().toLowerCase() === KNOWLEDGE_ARTICLE_RELATION_OVERVIEW;

export const isKnowledgeArticleRelationDisplayLink = (displayMode?: string | null): boolean =>
    displayMode?.trim().toLowerCase() === KNOWLEDGE_ARTICLE_RELATION_DISPLAY.LINK;

export const normalizeKnowledgeArticleRelationDisplay = (
    displayMode?: string | null
): KnowledgeArticleRelationDisplay =>
    isKnowledgeArticleRelationDisplayLink(displayMode)
        ? KNOWLEDGE_ARTICLE_RELATION_DISPLAY.LINK
        : KNOWLEDGE_ARTICLE_RELATION_DISPLAY.DIALOG;

/** The locations whose item page includes {@code LinkedKnowledgeArticlesInPage} — for now only the marketplace
 *  connector page. Mirrors {@code KnowledgeArticleRelationLookup.LINK_DISPLAY_LOCATIONS} in the backend. */
const KNOWLEDGE_ARTICLE_RELATION_IN_PAGE_LOCATIONS: readonly string[] = [RESOURCE_LOCATION.DATASERVICE];

/** Only a relation to one specific item of such a page can be shown in the page; an overview never can. */
export const isKnowledgeArticleRelationInPageAllowed = (location?: string | null, identifier?: string | null): boolean =>
    Boolean(location && identifier?.trim())
    && KNOWLEDGE_ARTICLE_RELATION_IN_PAGE_LOCATIONS.includes((location as string).toLowerCase())
    && !isKnowledgeArticleRelationOverview(identifier);

/** The display mode a relation to this target actually gets: in-page only where the page supports it. */
export const resolveKnowledgeArticleRelationDisplay = (
    location?: string | null,
    identifier?: string | null,
    displayMode?: string | null
): KnowledgeArticleRelationDisplay =>
    isKnowledgeArticleRelationInPageAllowed(location, identifier)
        ? normalizeKnowledgeArticleRelationDisplay(displayMode)
        : KNOWLEDGE_ARTICLE_RELATION_DISPLAY.DIALOG;

/** The knowledge base is only linkable as a module overview: an article is not linked to another article. */
export const isKnowledgeArticleRelationOverviewOnly = (location?: string | null): boolean =>
    location === RESOURCE_LOCATION.KNOWLEDGE_ARTICLE;

export const knowledgeArticleRelationDisplayLabelKey =(displayMode?: string | null): string =>
    `knowledgeBase.relations.display.${normalizeKnowledgeArticleRelationDisplay(displayMode)}`;

export const isKnowledgeArticleRelationType = (location: string): location is KnowledgeArticleRelationType =>
    (KNOWLEDGE_ARTICLE_RELATION_TYPES as readonly string[]).includes(location);

export const knowledgeArticleRelationLabelKey = (location: string): string =>
    `knowledgeBase.relations.type.${location}`;

export const knowledgeArticleRelationOverviewPath = (location: string): string | undefined => {
    switch (location) {
        case RESOURCE_LOCATION.KNOWLEDGE_ARTICLE:
            return KNOWLEDGE_BASE_LIST_PATH;
        case RESOURCE_LOCATION.ALERT:
            return ALERT_LIST_PATH;
        case RESOURCE_LOCATION.DASHBOARD:
            return "/dashboards";
        case RESOURCE_LOCATION.DATASOURCE:
        case RESOURCE_LOCATION.DATASERVICE:
            return MARKETPLACE_ROUTE;
        case RESOURCE_LOCATION.PROCESSFLOW:
            return PROCESSFLOW_LIST_PATH;
        case RESOURCE_LOCATION.OBJECTIVE:
            return OBJECTIVE_LIST_PATH;
        case RESOURCE_LOCATION.PROJECT:
            return PROJECT_LIST_PATH;
        default:
            return undefined;
    }
};

export const knowledgeArticleRelationPath = (location: string, identifier: string): string | undefined => {
    if (isKnowledgeArticleRelationOverview(identifier)) {
        return knowledgeArticleRelationOverviewPath(location);
    }
    switch (location) {
        case RESOURCE_LOCATION.KNOWLEDGE_ARTICLE:
            return knowledgeArticleDetailPath(identifier);
        case RESOURCE_LOCATION.ALERT:
            return alertDetailPath(identifier);
        case RESOURCE_LOCATION.DASHBOARD:
            return `/dashboard/${identifier}`;
        case RESOURCE_LOCATION.DATASOURCE:
            return marketplaceConnectionPath(identifier);
        case RESOURCE_LOCATION.DATASERVICE:
            return MARKETPLACE_ROUTE;
        case RESOURCE_LOCATION.PROCESSFLOW:
            return processflowDetailPath(identifier);
        case RESOURCE_LOCATION.OBJECTIVE:
            return objectiveDetailPath(identifier);
        case RESOURCE_LOCATION.PROJECT:
            return projectDetailPath(identifier);
        default:
            return undefined;
    }
};

export const toRelationSelectOption = (item: ReferenceModel): { label: string; value: string } => ({
    label: item.name?.trim() || item.code || item.uuid || "",
    value: item.uuid as string,
});

export const asRelationLocation = (location: string): ResourceLocation => location as ResourceLocation;

export const normalizeKnowledgeArticleRelationSections = (identifiers?: string[] | null): string[] =>
    [...new Set((identifiers ?? []).map((identifier) => identifier.trim()).filter(Boolean))];

export const filterKnowledgeArticleSections = (
    sections: IKnowledgeArticleSectionModel[],
    identifiers?: string[] | null
): IKnowledgeArticleSectionModel[] => {
    const allowed = normalizeKnowledgeArticleRelationSections(identifiers);
    if (allowed.length === 0) {
        return sections;
    }
    const allowedSet = new Set(allowed);
    return sections.filter((section) => allowedSet.has(section.uuid));
};
